import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import imageCompression from 'browser-image-compression'
import { uploadVerificationImage, fetchImages } from '../../services/gallery'
import { updateUser, getUser } from '../../services/oneUser'
import { Upload, Loader, AlertCircle } from 'lucide-react'

interface UploadVerificationImageProps {
  userId: string
  token: string
  onUploadSuccess: (imagePath: string) => void
  existingImage?: string | null
}

const compressionOptions = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  initialQuality: 0.7,
  fileType: 'image/jpeg',
}

// 🔥 Convertir PNG/GIF en JPEG via canvas
const convertToJpeg = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = event => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) return reject(new Error('Canvas context error'))

        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)

        canvas.toBlob(
          blob => {
            if (blob) {
              const newFile = new File(
                [blob],
                file.name.replace(/\.(png|gif)$/, '.jpg'),
                {
                  type: 'image/jpeg',
                },
              )
              resolve(newFile)
            } else {
              reject(new Error('Failed to convert to JPEG'))
            }
          },
          'image/jpeg',
          0.7,
        )
      }
      img.src = event.target?.result as string
    }
    reader.readAsDataURL(file)
  })
}

const UploadVerificationImage: React.FC<UploadVerificationImageProps> = ({
  userId,
  token,
  onUploadSuccess,
  existingImage,
}) => {
  const { t } = useTranslation()
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  if (existingImage) {
    return (
      <div className='mt-4'>
        <img
          src={existingImage}
          alt='Verification'
          className='max-w-md rounded-lg mx-auto'
        />
      </div>
    )
  }

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setIsUploading(true)
    setUploadError(null)

    try {
      let file = files[0]

      // Si le fichier est un PNG ou GIF, le convertir en JPEG
      if (file.type === 'image/png' || file.type === 'image/gif') {
        file = await convertToJpeg(file)
      }

      // Compression de l'image
      const compressedFile = await imageCompression(file, compressionOptions)
      const finalFile = compressedFile.size < file.size ? compressedFile : file

      // Upload du fichier
      const uploadedImages = await uploadVerificationImage(userId, token, [
        finalFile,
      ])

      if (uploadedImages && uploadedImages.length > 0) {
        const userData = await getUser(userId, token)

        const updateData = {
          ...userData.user,
          verifiedStatus: 'pending',
        }
        await updateUser(userId, token, updateData)

        const galleryImages = await fetchImages(userId, token)
        const verificationImg = galleryImages.find(img =>
          img.fileName.startsWith('(verification-'),
        )

        if (verificationImg) {
          onUploadSuccess(verificationImg.url)
        }
      }
    } catch (error) {
      console.error('Error in handleImageUpload:', error)
      setUploadError(
        error instanceof Error ? error.message : 'Error uploading image',
      )
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className='mt-6'>
      <div className='border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-violet-400 transition-colors'>
        <input
          type='file'
          accept='image/*'
          onChange={handleImageUpload}
          disabled={isUploading}
          className='hidden'
          id='file-upload'
        />
        <label
          htmlFor='file-upload'
          className='cursor-pointer flex flex-col items-center'
        >
          <Upload
            className={`w-12 h-12 mb-4 ${
              isUploading ? 'text-gray-400' : 'text-violet-600'
            }`}
          />
          <span className='text-lg font-medium text-gray-700 mb-2'>
            {t('upload-verification-photo')}
          </span>
          <span className='text-sm text-gray-500'>{t('click-to-upload')}</span>
        </label>
      </div>

      {isUploading && (
        <div className='mt-4 flex items-center justify-center text-violet-600'>
          <Loader className='w-5 h-5 mr-2 animate-spin' />
          <span>{t('uploading')}</span>
        </div>
      )}

      {uploadError && (
        <div className='mt-4 flex items-center text-red-500 bg-red-50 p-3 rounded-lg'>
          <AlertCircle className='w-5 h-5 mr-2' />
          <span>{uploadError}</span>
        </div>
      )}
    </div>
  )
}

export default UploadVerificationImage
